import { useEffect, useState } from 'react';
import { getQueryData, getPrimarySpecialtyRefURL, withCredentials, updateOnboardingStatusURL, getProfessionalInfoURL, instance as axiosInstance } from '../../../services';
import { useMutation, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { theme } from '../../../utils/theme/godzilla_light';
import { BackdropComponent, Controls } from '../../../components';
import { useForm } from 'react-hook-form';
import { formDefaults, formSchemaResolver } from './professionalInfo.helper';
import { monthOptions, yearOptions } from '../../../utils/date.utils';
import { InformationCardHOC } from '../components';
import OnboardGridLayout from '../components/OnboardGridLayout';
import AutoSearch from '../../../components/controls/autoSearch/AutoSearch';
import Grid from '@mui/material/Grid2'
import TranslationMessage from './transMessage';
import { useAppContext, useSnackbar } from '../../../contexts';
import { filterMonthDataById, modifyCommonDropDownData } from '../../common.helper';
import { validateErrorMsg } from '../../../utils/error.utils';

const communicationOptions = [
    { label: "Email", id: 'EMAIL' },
    { label: "Phone", id: 'PHONE' },
    { label: "SMS (Text)", id: 'SMS' },
]

export const ProfessionalInformation = () => {
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const { providerId, userData, fetchProvider } = useAppContext()

    const [specialtyList, setSpecialtyList] = useState<any[]>([])
    const [formLoading, setFormLoading] = useState(false)

    const {
        handleSubmit,
        reset: formReset,
        control,
        formState: { errors },
    } = useForm<any>({
        resolver: formSchemaResolver(t),
        defaultValues: formDefaults
    });

    const { data: specialtyRefData, isLoading: specialtyLoading } = useQuery("primarySpecialtyRefData", async () =>
        getQueryData(getPrimarySpecialtyRefURL, withCredentials())
    );

    const { data: professionalInfo } = useQuery("professionalInfoData", async () =>
        getQueryData(getProfessionalInfoURL(providerId), withCredentials())
    );

    useEffect(() => {
        if (specialtyRefData?.data?.length) {
            const formatedSpecialty = modifyCommonDropDownData(specialtyRefData?.data)
            setSpecialtyList(formatedSpecialty)
        }
    }, [specialtyRefData])

    useEffect(() => {
        const formValues = { ...formDefaults };
        if (professionalInfo?.data) {
            const prefillData = professionalInfo?.data
            const specialtyData = prefillData?.specialties?.length ? prefillData.specialties.filter((item: any) => item.type === "PRIMARY") : []
            const prefillCom: any = prefillData?.prefCommMethod ? communicationOptions.filter((el) => el.id === prefillData?.prefCommMethod) : communicationOptions

            formValues.primary_specialty = specialtyData?.length > 0 ? specialtyData[0].name : ''
            formValues.national_provider_id = prefillData?.npi
            formValues.practice_year = specialtyData?.length > 0 ? specialtyData[0]?.year?.toString() : ''
            formValues.practice_month = filterMonthDataById(specialtyData[0]?.month, 'id')
            formValues.preffered_communication = prefillCom[0].label
            formReset(formValues)
        }
        else {
            formValues.preffered_communication = communicationOptions[0].label
            formReset(formValues)
        }
    }, [professionalInfo, formReset])

    const updateOnboardingStatus = (params: any) => {
        setFormLoading(true);
        return axiosInstance().put(updateOnboardingStatusURL(providerId), params, withCredentials());
    };

    const handleError = (error: any) => {
        showSnackbar(validateErrorMsg(error?.data?.details[0]),"error");
    };

    const updateOnboardStatusApi = useMutation(updateOnboardingStatus, {
        onSuccess: fetchProvider,
        onError: handleError,
        onSettled: () => setFormLoading(false),
    });

    const submitMutation = useMutation(updateOnboardingStatus, {
        onSuccess: () => {
            const updateStatusParam = {
                appOnBoardings: userData?.appOnBoardings.map((step: any) =>
                    step.type === "PersonalInformation" ? { ...step, isDone: true } : step
                ),
            };
            updateOnboardStatusApi.mutate(updateStatusParam);
        },
        onError: handleError,
        onSettled: () => setFormLoading(false),
    });

    const submitForm = (formValues: any) => {
        if (!userData) {
            showSnackbar("Something went wrong!","error");
            return;
        }
        const prefComm = formValues?.preffered_communication === "SMS (Text)" ? "SMS" : formValues?.preffered_communication
        const professionalInfoParams = {
            ...userData,
            "npi": formValues?.national_provider_id,
            "prefCommMethod": prefComm?.toUpperCase(),
            "specialties": [
                {
                    "name": formValues.primary_specialty,
                    "type": "PRIMARY",
                    "year": formValues.practice_year,
                    "month": filterMonthDataById(formValues.practice_month, 'label')
                }
            ]
        }
        submitMutation.mutate(professionalInfoParams)
    }

    return (
        <>
            <OnboardGridLayout>
                <InformationCardHOC
                    infoTitle={t(TranslationMessage.PAGE_INFO_TITLE)}
                    buttonText={t(TranslationMessage.NEXT_BUTTON_LABEL)}
                    onClickBtn={handleSubmit(submitForm)}>
                    <Box component={'form'} sx={{ mt: "1rem" }}>
                        <AutoSearch
                            control={control}
                            name='primary_specialty'
                            label={t(TranslationMessage.PRIMARY_SPECIALTY)}
                            options={specialtyList}
                            defaultValue={''}
                            error={errors.primary_specialty || ''}
                            required />

                        <Controls.Input
                            name="national_provider_id"
                            label={t(TranslationMessage.NPI_LABEL)}
                            defaultValue={''}
                            control={control}
                            required
                            error={errors.national_provider_id || ''}
                        />

                        <Typography sx={{
                            ...theme.typography.components.inputLabel, textAlign: "left", mt: '12px'
                        }}>
                            {t(TranslationMessage.PRACTICE_DATE_INFO)}
                        </Typography>
                        <Typography sx={{
                            ...theme.typography.components.captionsText,
                            textAlign: "left"
                        }}>{t(TranslationMessage.PRACTICE_DATES)}</Typography>
                        <Grid container gap={'12px'}>
                            <Grid size={5}>
                                <AutoSearch
                                    control={control}
                                    name='practice_month'
                                    label=''
                                    options={monthOptions}
                                    defaultValue={''}
                                    error={errors.practice_month || ''}
                                    required
                                />
                            </Grid>
                            <Grid size={5}>
                                <AutoSearch
                                    control={control}
                                    name='practice_year'
                                    label=''
                                    options={yearOptions}
                                    defaultValue={''}
                                    sx={{ width: "120px" }}
                                    error={errors.practice_year || ''}
                                    required
                                />
                            </Grid>
                        </Grid>

                        <AutoSearch
                            control={control}
                            name='preffered_communication'
                            label={t(TranslationMessage.COMMUNICATION_TYPE)}
                            options={communicationOptions}
                            defaultValue={''}
                            sx={{ mt: '12px' }}
                            error={errors.preffered_communication || ''}
                            required
                        />
                    </Box>
                </InformationCardHOC>
            </OnboardGridLayout>
            <BackdropComponent showBackdrop={specialtyLoading || formLoading} />
        </>
    );
}